import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';

interface ErrorPageProps {
  status: number;
}

const errorConfig: Record<number, { title: string; description: string; icon: 'notFound' | 'server' | 'maintenance' }> = {
  404: {
    title: 'Página no encontrada',
    description: 'La página que buscás no existe o fue movida.',
    icon: 'notFound',
  },
  500: {
    title: 'Error del servidor',
    description: 'Algo salió mal de nuestro lado. Intentá de nuevo en unos minutos.',
    icon: 'server',
  },
  503: {
    title: 'En mantenimiento',
    description: 'Estamos haciendo mejoras. Volvemos en unos minutos.',
    icon: 'maintenance',
  },
};

const NotFoundIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 16.318A4.486 4.486 0 0012.016 15a4.486 4.486 0 00-3.198 1.318M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
  </svg>
);

const ServerIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

const MaintenanceIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
  </svg>
);

const icons = {
  notFound: NotFoundIcon,
  server: ServerIcon,
  maintenance: MaintenanceIcon,
};

const iconColors = {
  notFound: 'bg-gray-100 text-gray-400',
  server: 'bg-red-100 text-red-500',
  maintenance: 'bg-amber-100 text-amber-500',
};

export default function ErrorPage({ status }: ErrorPageProps) {
  const config = errorConfig[status] ?? errorConfig[500];
  const Icon = icons[config.icon];
  const colorClass = iconColors[config.icon];

  return (
    <MainLayout>
      <Head title={`${status} - ${config.title} | Abasto Virtual`}>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full ${colorClass}`}>
            <Icon className="h-10 w-10" />
          </div>

          <p className="mb-2 text-7xl font-bold text-gray-200">{status}</p>

          <h1 className="mb-3 text-xl font-semibold text-gray-800">{config.title}</h1>

          <p className="mb-8 text-gray-500">{config.description}</p>

          <Link
            href="/"
            className="inline-block rounded-xl bg-green-500 px-6 py-3 font-semibold text-white transition-colors hover:bg-green-600"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </MainLayout>
  );
}
